import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { useAuth } from '../contexts/AuthContext';
import BottomNavBar from '../components/BottomNavBar';
import SEOMeta from '../components/SEOMeta';
import PremiumUpgradeModal from '../components/PremiumUpgradeModal';
import LoginPromptModal from '../components/LoginPromptModal';

const PRIMARY = '#2DBCAF';

interface Dose {
  doseNumber: number;
  name: string;
  ageLabel: string;
  scheduledDate: string;
  status: 'pending' | 'completed' | 'skipped';
  isOverdue: boolean;
  completedDate?: string;
  location?: string;
  progressId?: string;
}

interface Vaccine {
  code: string;
  name: string;
  type: 'wajib' | 'anjuran';
  description: string;
  benefits: string[];
  sideEffects: string[];
  doses: Dose[];
  totalDoses: number;
  completedDoses: number;
  isComplete: boolean;
}

interface ScheduleData {
  child: { _id: string; name: string; birthDate: string; ageMonths: number };
  schedule: Vaccine[];
  stats: { totalWajib: number; completedWajib: number; percentWajib: number };
}

/* ── Styled ── */

const PageWrapper = styled.div`
  max-width: 512px;
  margin: 0 auto;
  min-height: 100dvh;
  background: linear-gradient(160deg, #e8f5f3 0%, #f6f8f8 40%, #fff5f0 100%);
  font-family: 'Plus Jakarta Sans', sans-serif;
  padding-bottom: 100px;
`;

const Header = styled.header`
  position: sticky;
  top: 0;
  z-index: 50;
  background: rgba(255,255,255,0.95);
  backdrop-filter: blur(12px);
  border-bottom: 1px solid #f1f5f9;
  padding: 14px 20px;
  display: flex;
  align-items: center;
  gap: 12px;
`;

const BackBtn = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  color: ${PRIMARY};
  padding: 0;
  display: flex;
  .material-symbols-outlined { font-size: 24px; }
`;

const HeaderTitle = styled.h1`
  font-size: 18px;
  font-weight: 700;
  color: #0f172a;
  margin: 0;
  flex: 1;
`;

/* Child Selector */
const ChildSelector = styled.div`
  margin: 16px;
  padding: 14px 16px;
  background: white;
  border-radius: 14px;
  border: 2px solid rgba(45,188,175,0.2);
  display: flex;
  align-items: center;
  gap: 12px;
  cursor: pointer;
`;

const ChildAvatar = styled.div`
  width: 44px;
  height: 44px;
  border-radius: 50%;
  background: linear-gradient(135deg, #fef3c7 0%, #fde68a 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  .material-symbols-outlined { font-size: 24px; color: #f59e0b; }
`;

const ChildInfo = styled.div`
  flex: 1;
  h3 { font-size: 15px; font-weight: 700; color: #0f172a; margin: 0; }
  p { font-size: 12px; color: #64748b; margin: 2px 0 0; }
`;

/* Progress Card */
const ProgressCard = styled.div`
  margin: 0 16px 16px;
  padding: 20px;
  background: linear-gradient(135deg, ${PRIMARY} 0%, #059669 100%);
  border-radius: 18px;
  color: white;
  box-shadow: 0 8px 24px rgba(45,188,175,0.3);
`;

const ProgressTitle = styled.p`
  font-size: 12px;
  font-weight: 600;
  opacity: 0.9;
  margin: 0 0 8px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const ProgressBar = styled.div`
  height: 10px;
  background: rgba(255,255,255,0.25);
  border-radius: 10px;
  overflow: hidden;
  margin-bottom: 10px;
`;

const ProgressFill = styled.div<{ percent: number }>`
  height: 100%;
  width: ${p => p.percent}%;
  background: white;
  border-radius: 10px;
  transition: width 0.5s ease;
`;

const ProgressText = styled.p`
  font-size: 14px;
  font-weight: 700;
  margin: 0;
`;

/* Tabs */
const Tabs = styled.div`
  display: flex;
  gap: 8px;
  padding: 0 16px;
  margin-bottom: 16px;
`;

const Tab = styled.button<{ $active?: boolean }>`
  flex: 1;
  padding: 10px;
  border-radius: 10px;
  font-size: 13px;
  font-weight: 600;
  border: none;
  cursor: pointer;
  background: ${p => p.$active ? PRIMARY : 'white'};
  color: ${p => p.$active ? 'white' : '#64748b'};
  box-shadow: ${p => p.$active ? '0 2px 8px rgba(45,188,175,0.3)' : 'none'};
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
`;

const TabPremiumBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 2px;
  background: linear-gradient(135deg, #FFA929 0%, #FF8C00 100%);
  color: white;
  font-size: 9px;
  font-weight: 700;
  padding: 2px 6px;
  border-radius: 4px;
  
  .material-symbols-outlined {
    font-size: 10px;
    font-variation-settings: 'FILL' 1;
  }
`;

/* Vaccine List */
const VaccineList = styled.div`
  padding: 0 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const VaccineCard = styled.div<{ $complete?: boolean }>`
  background: white;
  border-radius: 14px;
  padding: 16px;
  border: 1.5px solid ${p => p.$complete ? '#10b981' : '#f1f5f9'};
  box-shadow: 0 2px 8px rgba(0,0,0,0.04);
`;

const VaccineHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;
`;

const VaccineIcon = styled.div<{ $type: string }>`
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: ${p => p.$type === 'wajib' ? '#dbeafe' : '#fef3c7'};
  display: flex;
  align-items: center;
  justify-content: center;
  .material-symbols-outlined {
    font-size: 22px;
    color: ${p => p.$type === 'wajib' ? '#3b82f6' : '#f59e0b'};
  }
`;

const VaccineInfo = styled.div`
  flex: 1;
  h4 { font-size: 14px; font-weight: 700; color: #0f172a; margin: 0; }
  p { font-size: 11px; color: #64748b; margin: 2px 0 0; }
`;

const VaccineBadge = styled.span<{ $type: string }>`
  font-size: 9px;
  font-weight: 700;
  text-transform: uppercase;
  padding: 3px 8px;
  border-radius: 6px;
  background: ${p => p.$type === 'wajib' ? '#dbeafe' : '#fef3c7'};
  color: ${p => p.$type === 'wajib' ? '#3b82f6' : '#f59e0b'};
`;

const DoseList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const DoseItem = styled.div<{ $status: string; $overdue?: boolean }>`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  border-radius: 10px;
  background: ${p => 
    p.$status === 'completed' ? 'rgba(16, 185, 129, 0.08)' : 
    p.$overdue ? 'rgba(239, 68, 68, 0.08)' : '#f8fafc'};
  cursor: pointer;
  transition: background 0.2s;
  
  &:hover { background: ${p => p.$status === 'completed' ? 'rgba(16, 185, 129, 0.12)' : '#f1f5f9'}; }
`;

const DoseCheck = styled.div<{ $status: string; $overdue?: boolean }>`
  width: 24px;
  height: 24px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${p => 
    p.$status === 'completed' ? '#10b981' : 
    p.$overdue ? '#ef4444' : '#e2e8f0'};
  color: white;
  .material-symbols-outlined { font-size: 16px; }
`;

const DoseInfo = styled.div`
  flex: 1;
  h5 { font-size: 13px; font-weight: 600; color: #0f172a; margin: 0; }
  p { font-size: 11px; color: #64748b; margin: 2px 0 0; }
`;

/* Modal */
const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.5);
  display: flex;
  align-items: flex-end;
  justify-content: center;
  z-index: 200;
`;

const ModalSheet = styled.div`
  background: white;
  border-radius: 24px 24px 0 0;
  width: 100%;
  max-width: 512px;
  max-height: 85vh;
  overflow-y: auto;
  padding: 20px 20px 32px;
`;

const ModalHandle = styled.div`
  width: 40px;
  height: 4px;
  background: #e2e8f0;
  border-radius: 4px;
  margin: 0 auto 16px;
`;

const ModalTitle = styled.h3`
  font-size: 18px;
  font-weight: 700;
  color: #0f172a;
  margin: 0 0 8px;
  text-align: center;
`;

const ModalSubtitle = styled.p`
  font-size: 13px;
  color: #64748b;
  margin: 0 0 20px;
  text-align: center;
`;

const FormGroup = styled.div`
  margin-bottom: 16px;
`;

const Label = styled.label`
  display: block;
  font-size: 12px;
  font-weight: 600;
  color: #475569;
  margin-bottom: 6px;
`;

const Input = styled.input`
  width: 100%;
  padding: 12px 14px;
  border: 1.5px solid #e2e8f0;
  border-radius: 10px;
  font-size: 14px;
  font-family: inherit;
  box-sizing: border-box;
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;
  &:focus { outline: none; border-color: ${PRIMARY}; }
  
  &[type="date"] {
    min-height: auto;
    line-height: normal;
  }
  
  &::-webkit-date-and-time-value {
    text-align: left;
  }
  
  &::-webkit-calendar-picker-indicator {
    opacity: 0.6;
    cursor: pointer;
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 12px 14px;
  border: 1.5px solid #e2e8f0;
  border-radius: 10px;
  font-size: 14px;
  font-family: inherit;
  resize: none;
  height: 80px;
  &:focus { outline: none; border-color: ${PRIMARY}; }
`;

const ModalBtns = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 20px;
`;

const ModalBtn = styled.button<{ $primary?: boolean }>`
  flex: 1;
  padding: 14px;
  border-radius: 12px;
  font-size: 14px;
  font-weight: 600;
  font-family: inherit;
  cursor: pointer;
  border: ${p => p.$primary ? 'none' : '1.5px solid #e2e8f0'};
  background: ${p => p.$primary ? PRIMARY : 'white'};
  color: ${p => p.$primary ? 'white' : '#64748b'};
`;

/* Empty & Loading */
const EmptyState = styled.div`
  text-align: center;
  padding: 48px 24px;
  color: #64748b;
  .material-symbols-outlined { font-size: 48px; color: #cbd5e1; }
  h3 { font-size: 16px; font-weight: 700; color: #475569; margin: 12px 0 6px; }
  p { font-size: 13px; margin: 0; }
`;

const AddChildBtn = styled.button`
  margin-top: 16px;
  padding: 12px 24px;
  background: ${PRIMARY};
  color: white;
  border: none;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
`;

const ReferenceSection = styled.div`
  margin: 24px 16px 32px;
  padding: 16px;
  background: #f8fafc;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
`;

const ReferenceTitle = styled.h4`
  font-size: 12px;
  font-weight: 700;
  color: #64748b;
  margin: 0 0 10px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const ReferenceItem = styled.p`
  font-size: 11px;
  color: #64748b;
  margin: 4px 0;
  line-height: 1.5;
  
  a {
    color: ${PRIMARY};
    text-decoration: none;
    &:hover { text-decoration: underline; }
  }
`;

/* ── Helpers ── */

function formatDate(dateStr: string) {
  return new Date(dateStr).toLocaleDateString('id-ID', {
    day: 'numeric', month: 'long', year: 'numeric'
  });
}

/* ── Component ── */

export default function ImmunizationPage() {
  const navigate = useNavigate();
  const { user, activeChild } = useAuth();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<ScheduleData | null>(null);
  const [activeTab, setActiveTab] = useState<'wajib' | 'anjuran'>('wajib');
  const [showModal, setShowModal] = useState(false);
  const [showPremiumModal, setShowPremiumModal] = useState(false);
  const [selectedDose, setSelectedDose] = useState<{ vaccine: Vaccine; dose: Dose } | null>(null);
  const [formData, setFormData] = useState({ completedDate: '', location: '', notes: '' });
  const [saving, setSaving] = useState(false);
  const [anjuranRequiresPremium, setAnjuranRequiresPremium] = useState(true);

  // Check if user has active premium
  const isPremium = user?.subscription?.status === 'active' && 
    user?.subscription?.endDate && 
    new Date(user.subscription.endDate) > new Date();

  // Fetch setting for anjuran premium requirement
  useEffect(() => {
    fetch('/api/settings/public/immunization.anjuran.premium')
      .then(res => res.json())
      .then(data => {
        if (data.ok) {
          setAnjuranRequiresPremium(data.value ?? true);
        }
      })
      .catch(() => {});
  }, []);

  const fetchSchedule = async () => {
    if (!activeChild) return;
    
    setLoading(true);
    try {
      const res = await fetch(`/api/immunization/schedule/${activeChild._id}`, {
        credentials: 'include',
      });
      const json = await res.json();
      if (json.ok) {
        setData(json);
      }
    } catch (err) {
      console.error('Failed to fetch schedule:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSchedule();
  }, [activeChild]);

  const handleDoseClick = (vaccine: Vaccine, dose: Dose) => {
    // Check if anjuran vaccine requires premium (based on admin setting)
    if (vaccine.type === 'anjuran' && anjuranRequiresPremium && !isPremium) {
      setShowPremiumModal(true);
      return;
    }
    
    setSelectedDose({ vaccine, dose });
    setFormData({
      completedDate: dose.completedDate ? dose.completedDate.split('T')[0] : new Date().toISOString().split('T')[0],
      location: dose.location || '',
      notes: '',
    });
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!selectedDose || !activeChild) return;
    
    setSaving(true);
    try {
      const res = await fetch('/api/immunization/progress', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          childId: activeChild._id,
          vaccineCode: selectedDose.vaccine.code,
          doseNumber: selectedDose.dose.doseNumber,
          ...formData,
        }),
      });
      
      if (res.ok) {
        setShowModal(false);
        fetchSchedule();
      }
    } catch (err) {
      console.error('Failed to save:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleRemove = async () => {
    if (!selectedDose?.dose.progressId) return;
    
    if (!confirm('Hapus catatan imunisasi ini?')) return;
    
    setSaving(true);
    try {
      await fetch(`/api/immunization/progress/${selectedDose.dose.progressId}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      setShowModal(false);
      fetchSchedule();
    } catch (err) {
      console.error('Failed to remove:', err);
    } finally {
      setSaving(false);
    }
  };

  const filteredVaccines = data?.schedule.filter(v => v.type === activeTab) || [];

  return (
    <>
      <SEOMeta 
        title="Checklist Imunisasi - KinderGame.id"
        description="Pantau jadwal dan progres imunisasi anak sesuai program pemerintah Indonesia"
        path="/tumbuh-kembang/imunisasi"
      />
      <PageWrapper>
        <Header>
          <BackBtn onClick={() => navigate('/tumbuh-kembang')}>
            <span className="material-symbols-outlined">arrow_back</span>
          </BackBtn>
          <HeaderTitle>💉 Checklist Imunisasi</HeaderTitle>
        </Header>

        {!user ? (
          <>
            <EmptyState>
              <span className="material-symbols-outlined">vaccines</span>
              <h3>Checklist Imunisasi</h3>
              <p>Pantau jadwal imunisasi anak sesuai program pemerintah.</p>
            </EmptyState>
            <LoginPromptModal
              title="Ups! Kamu Belum Login"
              subtitle="Login untuk melihat checklist imunisasi anak"
              onClose={() => navigate('/tumbuh-kembang')}
            />
          </>
        ) : !activeChild ? (
          <EmptyState>
            <span className="material-symbols-outlined">child_care</span>
            <h3>Belum ada data anak</h3>
            <p>Tambahkan data anak terlebih dahulu untuk melihat jadwal imunisasi.</p>
            <AddChildBtn onClick={() => navigate('/profile')}>Tambah Data Anak</AddChildBtn>
          </EmptyState>
        ) : loading ? (
          <EmptyState>
            <span className="material-symbols-outlined">hourglass_top</span>
            <h3>Memuat...</h3>
          </EmptyState>
        ) : data && (
          <>
            {/* Child Selector */}
            <ChildSelector onClick={() => navigate('/profile')}>
              <ChildAvatar>
                <span className="material-symbols-outlined">face</span>
              </ChildAvatar>
              <ChildInfo>
                <h3>{data.child.name}</h3>
                <p>{data.child.ageMonths} bulan</p>
              </ChildInfo>
              <span className="material-symbols-outlined" style={{ color: '#94a3b8' }}>chevron_right</span>
            </ChildSelector>

            {/* Progress Card */}
            <ProgressCard>
              <ProgressTitle>Progress Imunisasi Wajib</ProgressTitle>
              <ProgressBar>
                <ProgressFill percent={data.stats.percentWajib} />
              </ProgressBar>
              <ProgressText>
                {data.stats.completedWajib} dari {data.stats.totalWajib} imunisasi ({data.stats.percentWajib}%)
              </ProgressText>
            </ProgressCard>

            {/* Tabs */}
            <Tabs>
              <Tab $active={activeTab === 'wajib'} onClick={() => setActiveTab('wajib')}>
                Wajib (Pemerintah)
              </Tab>
              <Tab $active={activeTab === 'anjuran'} onClick={() => setActiveTab('anjuran')}>
                Anjuran (IDAI)
                {anjuranRequiresPremium && (
                  <TabPremiumBadge>
                    <span className="material-symbols-outlined">crown</span>
                    Premium
                  </TabPremiumBadge>
                )}
              </Tab>
            </Tabs>

            {/* Vaccine List */}
            <VaccineList>
              {filteredVaccines.map(vaccine => (
                <VaccineCard key={vaccine.code} $complete={vaccine.isComplete}>
                  <VaccineHeader>
                    <VaccineIcon $type={vaccine.type}>
                      <span className="material-symbols-outlined">vaccines</span>
                    </VaccineIcon>
                    <VaccineInfo>
                      <h4>{vaccine.name}</h4>
                      <p>{vaccine.completedDoses}/{vaccine.totalDoses} dosis</p>
                    </VaccineInfo>
                    <VaccineBadge $type={vaccine.type}>
                      {vaccine.type}
                    </VaccineBadge>
                  </VaccineHeader>
                  
                  {/* Vaccine Description */}
                  {vaccine.description && (
                    <div style={{ 
                      fontSize: 12, 
                      color: '#64748b', 
                      marginBottom: 12,
                      lineHeight: 1.5,
                    }}>
                      {vaccine.description}
                    </div>
                  )}
                  
                  {/* Benefits & Side Effects */}
                  <div style={{ 
                    display: 'grid', 
                    gridTemplateColumns: '1fr 1fr', 
                    gap: 8, 
                    marginBottom: 12,
                    fontSize: 11,
                  }}>
                    {vaccine.benefits?.length > 0 && (
                      <div style={{ 
                        padding: '8px 10px', 
                        background: '#f0fdf4', 
                        borderRadius: 8,
                        borderLeft: '3px solid #22c55e',
                      }}>
                        <div style={{ fontWeight: 600, color: '#166534', marginBottom: 4 }}>✅ Manfaat</div>
                        {vaccine.benefits.slice(0, 2).map((b: string, i: number) => (
                          <div key={i} style={{ color: '#15803d' }}>• {b}</div>
                        ))}
                      </div>
                    )}
                    {vaccine.sideEffects?.length > 0 && (
                      <div style={{ 
                        padding: '8px 10px', 
                        background: '#fefce8', 
                        borderRadius: 8,
                        borderLeft: '3px solid #eab308',
                      }}>
                        <div style={{ fontWeight: 600, color: '#854d0e', marginBottom: 4 }}>⚠️ Efek Samping</div>
                        {vaccine.sideEffects.slice(0, 2).map((s: string, i: number) => (
                          <div key={i} style={{ color: '#a16207' }}>• {s}</div>
                        ))}
                      </div>
                    )}
                  </div>
                  
                  <DoseList>
                    {vaccine.doses.map(dose => (
                      <DoseItem 
                        key={dose.doseNumber}
                        $status={dose.status}
                        $overdue={dose.isOverdue}
                        onClick={() => handleDoseClick(vaccine, dose)}
                      >
                        <DoseCheck $status={dose.status} $overdue={dose.isOverdue}>
                          <span className="material-symbols-outlined">
                            {dose.status === 'completed' ? 'check' : dose.isOverdue ? 'priority_high' : 'schedule'}
                          </span>
                        </DoseCheck>
                        <DoseInfo>
                          <h5>{dose.name}</h5>
                          <p>
                            {dose.status === 'completed' 
                              ? `✅ ${formatDate(dose.completedDate!)}${dose.location ? ` • ${dose.location}` : ''}`
                              : dose.isOverdue 
                                ? `⚠️ Terlambat (jadwal: ${dose.ageLabel})`
                                : `📅 ${dose.ageLabel}`
                            }
                          </p>
                        </DoseInfo>
                      </DoseItem>
                    ))}
                  </DoseList>
                </VaccineCard>
              ))}
            </VaccineList>

            {/* Reference Section */}
            <ReferenceSection>
              <ReferenceTitle>📚 Referensi Data</ReferenceTitle>
              <ReferenceItem>• Permenkes No. 12 Tahun 2017 tentang Imunisasi</ReferenceItem>
              <ReferenceItem>• <a href="https://idai.or.id" target="_blank" rel="noopener noreferrer">IDAI</a> - Jadwal Imunisasi Anak 2023</ReferenceItem>
              <ReferenceItem>• Kemenkes RI - Program Imunisasi Dasar Lengkap</ReferenceItem>
              <ReferenceItem style={{ marginTop: 8, fontStyle: 'italic' }}>
                * Data ini bersifat informatif. Konsultasikan dengan dokter/bidan untuk jadwal imunisasi yang tepat.
              </ReferenceItem>
            </ReferenceSection>
          </>
        )}
      </PageWrapper>

      <BottomNavBar />

      {/* Record Modal */}
      {showModal && selectedDose && (
        <ModalOverlay onClick={() => setShowModal(false)}>
          <ModalSheet onClick={e => e.stopPropagation()}>
            <ModalHandle />
            <ModalTitle>{selectedDose.dose.name}</ModalTitle>
            <ModalSubtitle>{selectedDose.vaccine.name}</ModalSubtitle>

            <FormGroup>
              <Label>Tanggal Imunisasi</Label>
              <Input 
                type="date" 
                value={formData.completedDate}
                onChange={e => setFormData({ ...formData, completedDate: e.target.value })}
              />
            </FormGroup>

            <FormGroup>
              <Label>Lokasi (Puskesmas/RS/Klinik)</Label>
              <Input 
                type="text"
                placeholder="Contoh: Puskesmas Malang"
                value={formData.location}
                onChange={e => setFormData({ ...formData, location: e.target.value })}
              />
            </FormGroup>

            <FormGroup>
              <Label>Catatan (opsional)</Label>
              <TextArea 
                placeholder="Catatan tambahan..."
                value={formData.notes}
                onChange={e => setFormData({ ...formData, notes: e.target.value })}
              />
            </FormGroup>

            <ModalBtns>
              {selectedDose.dose.status === 'completed' && (
                <ModalBtn onClick={handleRemove} disabled={saving}>
                  Hapus
                </ModalBtn>
              )}
              <ModalBtn onClick={() => setShowModal(false)}>Batal</ModalBtn>
              <ModalBtn $primary onClick={handleSave} disabled={saving}>
                {saving ? 'Menyimpan...' : 'Simpan'}
              </ModalBtn>
            </ModalBtns>
          </ModalSheet>
        </ModalOverlay>
      )}

      {/* Premium Upgrade Modal */}
      <PremiumUpgradeModal
        isOpen={showPremiumModal}
        onClose={() => setShowPremiumModal(false)}
        featureName="Checklist Imunisasi Anjuran (IDAI)"
      />
    </>
  );
}
